import { classNames, Component, h, OmiProps, signal, tag } from "omi";

import { COMMUNITY_LINK_WITH_ERROR_UTM } from "@/globals/links";
import { NWButton, openModal } from "@/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

export type NWImageProps = {
  src: string;
  alt: string;
  withBorder?: boolean;
  withSpacing?: boolean;
};

@tag("nw-image")
export class NWImage extends Component<NWImageProps> {
  static css = [tailwind];

  static propTypes = {
    src: String,
    alt: String,
    withBorder: Boolean,
    withSpacing: Boolean,
  };

  static defaultProps = {
    withSpacing: true,
  };

  private isLoaded = signal(false);
  private isError = signal(false);

  private openImageZoom(src: string) {
    openModal("imageZoom", { href: src });

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackImageZoomed(Store.project.id.value!, src);
    });
  }

  private handleImageLoad = () => {
    this.isLoaded.value = true;
  };

  private handleImageError = () => {
    this.isError.value = true;
  };

  render({ withSpacing = true, ...props }: OmiProps<NWImageProps>) {
    const ready = this.isLoaded.value && !this.isError.value;

    return (
      <div class={classNames({ "py-10": withSpacing })}>
        <figure
          class={classNames("flex items-center justify-center flex-col", {
            "cursor-zoom-in": ready,
          })}
          onClick={ready ? () => this.openImageZoom(props.src) : undefined}
        >
          <div
            class={classNames("w-full rounded-2xl overflow-hidden", {
              "bg-gray-200 animate-pulse h-80": !this.isLoaded.value && !this.isError.value,
              border: props.withBorder,
            })}
          >
            {this.isError.value ? (
              <div class="w-full h-80 bg-gray-50 rounded-2xl border border-gray-200 flex items-center justify-center text-error-600 text-sm font-semibold flex-col gap-4">
                <p class="text-center">
                  Oops! The image failed to load. <br />
                  Please try refreshing and{" "}
                  <a
                    href={COMMUNITY_LINK_WITH_ERROR_UTM}
                    target="_blank"
                    class="underline underline-offset-2 transition-colors hover:text-error-600"
                  >
                    let us know
                  </a>{" "}
                  if the problem persists.
                </p>
                <NWButton size="fit" type="secondary-color" onClick={() => window.location.reload()}>
                  <span className="text-xs">Refresh</span>
                </NWButton>
              </div>
            ) : (
              <img
                class={classNames("w-full", {
                  "animate-fadeInFast": this.isLoaded.value,
                  "opacity-0": !ready,
                })}
                src={props.src}
                alt={props.alt}
                loading="lazy"
                onLoad={this.handleImageLoad}
                onError={this.handleImageError}
              />
            )}
          </div>
          <figcaption class="mt-4 text-sm text-tertiary">{props.alt}</figcaption>
        </figure>
      </div>
    );
  }
}
